import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation } from "@apollo/client";
import { UPDATE_EMPLOYEE_DETAILS } from "../../../../../../../apis/organization-apis/people-apis/peopleApis";
import ConfirmSnackbar from "../../../../../../../components/Snackbar/ConfirmSnackbar";
import moment from "moment";
import DatePicker from "../../../../../../../components/DatePicker/DatePicker";
import { formatDateForInput } from "../../../../../../../utils/dateUtils";

const EditStartDateDialog = ({
  open,
  onClose,
  employee,
  currentStartDate,
  refetch,
  hasHrmsIntegration = false,
}) => {
  const [updateEmployee, { loading: updating }] = useMutation(UPDATE_EMPLOYEE_DETAILS);
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [startDate, setStartDate] = useState("");

  useEffect(() => {
    if (open) {
      setStartDate(formatDateForInput(currentStartDate));
    } else {
      setStartDate("");
    }
  }, [currentStartDate, open]);

  const handleSave = async () => {
    if (!employee?.id) return;

    try {
      const employeeEmail = employee.work_email || employee.emails?.find((e) => e.type === "work")?.email || employee.emails?.[0]?.email || "";

      if (!employeeEmail) {
        setSnackbarInfo({
          message: "Employee email is required but not found",
          vertical: "top",
          horizontal: "right",
        });
        return;
      }

      if (!startDate || !startDate.trim()) {
        setSnackbarInfo({
          message: "Start date is required",
          vertical: "top",
          horizontal: "right",
        });
        return;
      }

      const date = moment(startDate);
      if (!date.isValid()) {
        setSnackbarInfo({
          message: "Invalid date format",
          vertical: "top",
          horizontal: "right",
        });
        return;
      }

      // Check if termination date exists and is before start date
      if (employee?.termination_date) {
        const termDate = moment(employee.termination_date);
        if (termDate.isValid() && date.isAfter(termDate)) {
          setSnackbarInfo({
            message: "Start date cannot be after termination date",
            vertical: "top",
            horizontal: "right",
          });
          return;
        }
      }

      const formattedDate = date.format("YYYY-MM-DD");

      const response = await updateEmployee({
        variables: {
          input: [
            {
              id: employee.id,
              email: employeeEmail,
              start_date: formattedDate,
            },
          ],
        },
      });

      if (response?.data?.updateEmployeeDetails?.success) {
        setSnackbarInfo({
          message: "Start date updated successfully",
          vertical: "top",
          horizontal: "right",
        });
        if (refetch) {
          await refetch();
        }
        onClose();
      } else {
        setSnackbarInfo({
          message: response?.data?.updateEmployeeDetails?.message || "Failed to update start date",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (error) {
      setSnackbarInfo({
        message: error?.message || "Failed to update start date",
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1.5,
            pt: 2.5,
            px: 3,
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Typography variant="h5" fontWeight={600} color="#111827">
            Edit Start Date
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: "#6b7280" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 3, pb: 2 }}>
          {hasHrmsIntegration && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Start date is managed by HRMS integration and cannot be edited manually.
            </Alert>
          )}
          <Box sx={{ pt: 2 }}>
            <DatePicker
              fullWidth
              label="Start Date"
              value={startDate}
              onChange={(date) => setStartDate(date)}
              disabled={updating || hasHrmsIntegration}
              maxDate={employee?.termination_date}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 2, borderTop: "1px solid #e5e7eb" }}>
          <Button
            onClick={onClose}
            disabled={updating}
            sx={{
              color: "#6b7280",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#f3f4f6",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={updating || !startDate || hasHrmsIntegration}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </>
  );
};

export default EditStartDateDialog;
